import axios from 'axios';
import keyMirror from 'keymirror';
import serialize from 'serialize-javascript';
import {
  getHostApi,
  getHostApiCognito,
  getHostApiMicros,
  getHostApiV1,
  getHostApiV4,
  getHostApiV5,
  getHostReservationPnr,
} from './api-config';
import { getRefundEndpoints } from './refunds-registry';
import { generateJwtToken } from '../redux/actions/jwt/jwt-action-creators';
import { isPnrOrEticket, setCookie } from '../scripts/utilities/helpers';
import safeLocalStorage from '../scripts/utilities/safe-local-storage';
import regexMap from '../scripts/constants/regex-map';
import errorCodes from '../scripts/components/error-handling/error-codes';
import transformAssociationFFNumber from './transformers/association-ffnumber';
import { getHeaderMarketingCloud } from '../scripts/utilities/interaction-studio-utilities';
import serviceCognitoV4 from '../scripts/constants/services-cognito-v4';
import serviceCognitoV5 from '../scripts/constants/services-cognito-v5';
import { sendAnalytics } from '../scripts/analytics/tracker';
import manageTracking from '../scripts/analytics/creators/manage';
import store from '../redux/store';
import POD_CODES from '../scripts/constants/pod-codes';
import { getErrorCms } from '../cms-wrapper/cms';
// import { getPingOneAccessToken, clearPingOneAccessToken } from './pingone-token';
import { decryptReservationPayload } from './aes-utils';

const {
  REACT_APP_CLIENT_COGNITO_ID,
  REACT_APP_CLIENT_COGNITO_SECRET,
  REACT_APP_CONTACTABILITY_ID,
  REACT_APP_CONTACTABILITY_SECRET,
  REACT_APP_INTERACTION_STUDIO_API_SECRET,
  REACT_APP_INTERACTION_STUDIO_API_TOKEN,
  REACT_APP_INTERACTION_STUDIO_API_URL,
  REACT_APP_REFUND_API_ID,
  REACT_APP_TOKEN_AUTH_COGNITO,
  REACT_APP_TOKEN_AUTH_COGNITO_BKN,
  REACT_APP_TOKEN_RAM,
  REACT_APP_UNHANDLED_REFUND,
} = process.env;

const HOST = getHostApi(window.location.host);
const HOSTV1 = getHostApiV1(window.location.host);
const HOSTV4 = getHostApiV4(window.location.host);
const HOSTV5 = getHostApiV5(window.location.host);
const HOSTCOGNITO = getHostApiCognito(window.location.host);
const HOSTMICRO = getHostApiMicros();

const template = require('string-template');
const METHOD = keyMirror({
  'GET': null,
  'PUT': null,
  'POST': null,
  'DELETE': null,
  'PATCH': null,
});

function getEnviroment() {
  const env = location.hostname;
  const isBetaProd = /^((www|beta|alpha).)*(aeromexico)\.com$/gi.test(env);

  return isBetaProd;
}

export function getBasicTokenCognito(token) {
  try {
    const tokenSecret = JSON.parse(atob(token));
    const optionHeader = getEnviroment() ? 'prod' : 'cert';
    return tokenSecret[optionHeader];
  } catch (error) {
    return '';
  }
}

function isTokenCognito(endpoint) {
  const isCognito = serviceCognitoV4.includes(endpoint) ? serviceCognitoV4.includes(endpoint) : serviceCognitoV5.includes(endpoint);
  return isCognito;
}

function getUrl(route, opts, isCognito = false, isMicro = false, isBff = false) {
  let NEWHOST = HOSTV4;
  serviceCognitoV5.map(ho => {
    NEWHOST = route.includes(ho) ? HOSTV5 : HOSTV4;
  });

  if (route.includes('/contactability') || route.includes('/am-grant/grantAccess') || route.includes('/oauth2/token?grant_type=client_credentials') || route.includes('/update-consent')) {
    return (isCognito ? HOSTCOGNITO : HOSTV1) + template(route, opts);
  } else {
    if (isMicro) {
      const resolvedRoute = template(route, opts);
      // En modo BFF: si la ruta ya trae protocolo (https://...) se usa tal cual;
      // si empieza con // se antepone solo el protocolo para no duplicarlo.
      const bffUrl = /^https?:\/\//i.test(resolvedRoute)
        ? resolvedRoute
        : `https:${resolvedRoute}`;
      const fullUrl = isBff ? bffUrl : HOSTMICRO + resolvedRoute;
      return fullUrl;
    } else {
      return (isCognito ? NEWHOST : HOST) + template(route, opts);
    }
  }
}

async function doRequest(
  endpoint,
  opts,
  headersRequest = null,
  requestMethod = 'get',
  data = null,
  typeToken = 'MYB',
  isCognito = false,
  isRefund = false,
  isMicro = false,
  pnrlookup = false,
  signal = undefined, // Nuevo parámetro opcional para AbortController
  isBff = false
) {
  const isBkn = typeToken === 'BKN';
  let errorResponse = '';
  try {
    const dataRequest = {
      method: requestMethod,
      url: !isRefund ? getUrl(endpoint, opts, isCognito, isMicro, isBff) : endpoint,
    };
    if (headersRequest) {
      dataRequest.headers = headersRequest;
    }

    if (data) {
      dataRequest.data = data;
    }
    if (signal) {
      dataRequest.signal = signal; // Soporte para AbortController
    }
    return await axios(dataRequest)
      .then((response) => {
        if (response?.data?.reason && Number(response?.data?.httpCode) >= 400 && endpoint === '/contactability/digital') {
          const error = {
            response: {
              data: {
                reason: response.data.reason,
              },
              status: Number(response.data.httpCode),
            },
          };
          throw error;
        }

        /**
        *  Upgrade vocuher success response se retorna el response compleeto no solo la data de la respuesta
        *  para poder manejar los posibles errores en redencio que este micro responda y
        *  el modal de error a mostrar sin enviar metricas de errro ya que la respuesta incluira
        *  redecniones exitosas y fallidas.
        */
        if (endpoint.includes('/uc/upgrade-voucher') && response?.config?.method === 'post') {
          return response;
        }
        return response.data;
      })
      .catch(async(error) => {
        const { response } = error;
        const statusCode = response?.status || null;
        // Manejo de cancelación por AbortController
        if (signal && signal.aborted) {
          return {
            actionable: 'CONTINUE',
            code: '17111202',
            msg: errorCodes.REQUEST_CANCELLED,
            extraInfo: {
              'debugging_stuff_here': error?.response?.data?.extraInfo?.debugging_stuff_here || 'invalid_request',
            },
            'status': 503,
          };
        }

        if (statusCode === null) {
          const isresponseHTML = regexMap.IS_HTML.test(response);
          const codeClasificationErrors = isresponseHTML ? '17111223' : '17111224';
          const msjClasificationErrors = isresponseHTML ? errorCodes.SERVER_ACCESS_UNAVAILABLE : errorCodes.SERVICE_UNAVAILABLE;

          return {
            '_meta': {
              'class': 'Incident',
            },
            'code':  codeClasificationErrors,
            'actionable': 'ABORT',
            'msg': msjClasificationErrors,
            'extraInfo': {
              'debugging_stuff_here': error?.response?.data?.extraInfo?.debugging_stuff_here || 'invalid_request',
            },
            'status': 503,
          };
        }

        // Interceptar errores de infraestructura 502/504 (proxy layer)
        if (statusCode === 502 || statusCode === 504) {
          return {
            '_meta': {
              'class': 'Incident',
            },
            'code': statusCode,
            'actionable': 'ABORT',
            'msg': errorCodes.SERVICE_UNAVAILABLE,
            'extraInfo': {
              'debugging_stuff_here': response?.statusText || 'invalid_request',
            },
            'status': statusCode,
          };
        }

        if (error?.response?.data?.reason) {
          const errorFlow = error?.response?.config?.headers?.flow;

          try {
            const AMOptData = localStorage.getItem('AMOptData');
            const dataUtil = JSON.parse(AMOptData);
            const language = dataUtil.general.language;
            const errorCms = await getErrorCms(error?.response?.data?.reason, language);

            return {
              '_meta': {
                'class': 'Incident',
              },
              'event': 'error',
              'type': 'NA',
              'statusCode': error?.response?.data?.httpCode, // Vine del API
              'code': error?.response?.data?.reason, // Vine del API
              'actionType': 'NA',
              'actionable': errorCms?.actionable.toUpperCase() || 'ABORT', // Viene de CMS
              'errorMsg': errorCms?.descriptionError || 'NA', // Viene de CMS, sino está cargado se vá como NA
              'errorExtraInfo': `WEB|${errorFlow}-status`, // El web es por default, el flow se toma del header request
              'errorUrl': window.location.pathname,  // Toma la ruta del urlPath
              'codeAction': errorCms?.userExperience?.type, // Viene de cms
            };
          } catch (error) {
            console.warn(error); // eslint-disable-line no-console
            return error;
          }
        }
        const dispatch = store.dispatch;
        const isProfileEndpoint = endpoint?.includes('/enrollment/') || endpoint?.includes('/profile/user-profile-sign-in');
        const isPayment = endpoint?.includes('/manage/po');
        let errorObject = {};

        if (endpoint === '/contactability/digital') {
          const errorMessage = error?.response?.data?.reason.split('.').pop();
          errorObject = {
            actionable: 'CONTINUE',
            code: error?.response?.data?.reason,
            statusCode: error?.response?.status,
            extraInfo: errorMessage,
          };
          const requestMetric = {
            errorData: {
              actionable: 'CONTINUE',
              extraInfo: {
                'debugging_stuff_here': errorMessage,
              },
              msg: errorMessage,
              status: error?.response?.status,
            },
          };
          dispatch(sendAnalytics({
            eventCreator: manageTracking.errorApiServiceTrackingFatal(requestMetric, error.response.data.reason, error.response.status, 'RETRIVE_CONTACTABILITY'),
          }));
        }

        if (!isProfileEndpoint) {
          if ((errorObject || error?.response) && ![502, 503, 504].includes(error?.response?.status)) {
            const subFunnelName = isPayment ? 'Payment' : pnrlookup ? 'PnrLookup' : null;
            dispatch(sendAnalytics({
              eventCreator: manageTracking.webErrorSchemaTracking(errorObject ? errorObject : error?.response, subFunnelName),
            }));
          }
        }

        const errorFlow = error?.response?.config?.headers?.flow;
        if (regexMap.IS_HTML.test(error.request.response) || regexMap.IS_HTML.test(error.response)) {
          return {
            '_meta': {
              'class': 'Incident',
            },
            'code': error && error.response && error.response.status ? error.response.status : 503,
            'actionable': (error.response.actionable) ? error.response.actionable : 'ABORT',
            'msg': errorCodes.API_SERVICE_UNAVAILABLE,
            'extraInfo': {
              'debugging_stuff_here': error?.response?.statusText || `WEB|${errorFlow}-status` || null,
            },
            'status': error && error.response && error.response.status ? error.response.status : 503,
          };
        }

        const isProfile = safeLocalStorage.getItem('isLoginForm');
        const isLogoutFail = safeLocalStorage.getItem('logoutFail');

        // catch error status code 400 - 599
        if (endpoint === '/am-grant/grantAccess' || endpoint === '/oauth2/token?grant_type=client_credentials') {
          if (error && error.response && error.response.data && error.response.data._meta &&
            error.response.data._meta.class === 'Incident' && error.response.data.code >= 400 &&
            error.response.data.code <= 599) {
            const errorOAuth = {
              '_meta': {
                'class': 'Incident',
              },
              'code': isProfile || isLogoutFail ? '15111803' : isBkn ? '17111803' : '13111801',
              'actionable': (error.response.data.actionable) ? error.response.data.actionable : 'ABORT',
              'msg': (error.response.data.msg) ? error.response.data.msg : 'Bad Request',
              'extraInfo': {
                'debugging_stuff_here': error?.response?.data?.extraInfo?.debugging_stuff_here || 'invalid_request',
              },
              'status': error?.response?.status || 503,
            };
            if (isProfile || isLogoutFail) {
              const manageApiErrorCookie = {
                code: errorOAuth.code,
                actionable: errorOAuth.actionable,
                msg: errorOAuth.msg,
                extraInfo: errorOAuth.extraInfo.debugging_stuff_here,
                errorInfo: errorOAuth,
              };

              setCookie('manageApiError', serialize(manageApiErrorCookie, { isJson: true }));
              return errorOAuth;
            }
            return errorOAuth;
          }
        }

        // Error unauthorized
        if (error && error.response && error.response.data && error.response.data.code && (error.response.data.code === '13111801' || error.response.data.code === '15111801')) {
          if (isProfile) {
            const errorCode = (endpoint === '/am-grant/grantAccess' || endpoint === '/oauth2/token?grant_type=client_credentials') ? '15111801' : '15111802';
            const errorOAuth = {
              '_meta': {
                'class': 'Incident',
              },
              'code': errorCode,
              'actionable': (error.response.data.actionable) ? error.response.data.actionable : 'ABORT',
              'msg': (error.response.data.msg) ? error.response.data.msg : 'Bad Request',
              'extraInfo': {
                'debugging_stuff_here': error?.response?.data?.extraInfo?.debugging_stuff_here || 'invalid_request',
              },
              'status': error?.response?.status || 503,
            };
            return errorOAuth.code;
          } else {
            const errorCode = (endpoint === '/am-grant/grantAccess' || endpoint === '/oauth2/token?grant_type=client_credentials') ? '13111801' : '13111802';
            return {
              '_meta': {
                'class': 'Incident',
              },
              'code': errorCode,
              'actionable': (error.response.data.actionable) ? error.response.data.actionable : 'ABORT',
              'msg': (error.response.data.msg) ? error.response.data.msg : 'Bad Request',
              'extraInfo': {
                'debugging_stuff_here': error?.response?.data?.extraInfo?.debugging_stuff_here || 'invalid_request',
              },
              'status': error?.response?.status || 503,
            };
          }
        }

        if (endpoint === '/profile/user-profile-sign-in' || endpoint === '/profile/user-profile-sign-out' || ((endpoint === '/am-grant/grantAccess' || endpoint === '/oauth2/token?grant_type=client_credentials') && isProfile) || ((endpoint === '/am-grant/grantAccess' || endpoint === '/oauth2/token?grant_type=client_credentials') && isLogoutFail)) {
          const datoIncident = JSON.parse(error.request.response);
          const isDesconected = datoIncident && datoIncident.extraInfo ? false : true;

          let urlGrant = '/am-grant/grantAccess';

          if (isCognito) {
            urlGrant = '/oauth2/token?grant_type=client_credentials';
          }

          if (isDesconected) {
            const errorOAuth = {
              '_meta': {
                'class': 'Incident',
              },
              'code': '15111801',
              'actionable': 'ABORT',
              'msg': 'Unable to identify proxy for host: secure and url: ' + urlGrant,
              'extraInfo': {
                'debugging_stuff_here': 'messaging.adaptors.http.flow.ApplicationNotFound',
              },
              'status': 404,
            };

            setCookie('manageApiError', serialize(errorOAuth, { isJson: true }));
            // console.warn(error); // eslint-disable-line no-console
            // retornar codigo de error para manejarlo en el modal de login y la metrica correspondiente

            return errorOAuth;
          } else {
            const incidentData = JSON.parse(error.request.response);

            // creando la cookie para manejar el contenido del mensaje en metrica error
            const manageApiErrorCookie = {
              code: incidentData.code,
              actionable: incidentData.actionable,
              msg: incidentData.msg,
              extraInfo: incidentData?.extraInfo?.debugging_stuff_here,
              errorInfo: {
                ...incidentData,
                status: error.request.status,
              },
            };

            setCookie('manageApiError', serialize(manageApiErrorCookie, { isJson: true }));
            // console.warn(error); // eslint-disable-line no-console
            // retornar codigo de error para manejarlo en el modal de login y la metrica correspondiente

            return incidentData.code;
          }
        }

        const err = error.response.data;
        let errorInfo = '';

        if (typeof err == 'string') {
          const jsonString = err.replace(/"timestamp":\s*([^,]+),/, '"timestamp": "$1",');
          errorInfo = JSON.parse(jsonString);
        } else {
          errorInfo = error.response.data;
        }

        errorInfo.status = (error.response.status) ? error.response.status : 503;
        return errorInfo;
      });
  } catch (error) {
    console.warn(error); // eslint-disable-line no-console
    errorResponse = error && error.response && error.response.data ? error.response.data : null;
    return errorResponse;
  }
}

const manageGrantAccessDisable = (error, typeToken = 'MYB') => {
  return {
    '_meta': {
      'class': 'Incident',
    },
    'code': typeToken === 'PRO' ? '15111801' : typeToken === 'BKN' ? '17111801' : '13111801',
    'actionable': 'ABORT',
    'msg': error?.fault?.faultstring || 'Bad Request',
    'extraInfo': {
      'debugging_stuff_here': error?.fault?.detail?.errorcode || 'invalid_request',
    },
    'status': error?.status ? error.status : 503,
  };
};

export async function getAirports(opts) {
  const endPoint = '/airports';
  const isCognito = isTokenCognito(endPoint);
  const token = getPromiseGrant(isCognito, 'MYB');

  return await token.then(async responseToken => {
    const grantAccess = isCognito ? responseToken?.access_token : responseToken?.grantAccess;
    const headersRequest = addHeaders(opts, null, grantAccess);
    return await doRequest(
      `${endPoint}?store={storeCode}&pos={posCode}`,
      opts,
      headersRequest.headers,
      'get',
      null,
      'MYB',
      isCognito
    ).then(response => {
      return response;
    }).catch(error => {
      return error;
    });
  }).catch(err => {
    return err;
  });
}

export async function getPromiseGrant(isCognito, env) {
  const env_ = `MYB2_TOKEN_AUTH_COGNITO_${env}`;
  let tokenStorage = null;
  try {
    tokenStorage = JSON.parse(localStorage.getItem(env_));
  } catch (e) {
    // Token cacheado corrupto: se ignora y se solicita uno nuevo.
    tokenStorage = null;
  }

  const minTimeToken = 5;
  const maxTimeToken = 30;
  let dateDiff_ = 0;
  let expiracion = '';

  if (tokenStorage?.env === env_) {
    try {
      const now = new Date();
      const jwtPayload = tokenStorage?.access_token?.split('.')?.[1];
      if (jwtPayload) {
        const tokenBody = JSON.parse(atob(jwtPayload));
        expiracion = new Date(tokenBody.exp * 1000);
        dateDiff_ = dateDiff(expiracion, now);
      } else {
        // access_token ausente o no es un JWT válido: fuerza renovación.
        tokenStorage = null;
      }
    } catch (e) {
      // access_token inválido/no decodificable: fuerza renovación.
      tokenStorage = null;
    }
  }

  if (tokenStorage?.env === env_ && (dateDiff_ > minTimeToken && dateDiff_ <= maxTimeToken)) {
    return await tokenStorage;
  } else {
    const requestToken = getAmGrant(env, isCognito);
    return await requestToken.then(async responseToken => {
      if (responseToken) {
        const myToken = {
          'env': env_,
          'access_token': responseToken?.access_token,
          'expires_in': responseToken?.expires_in,
          'token_type': responseToken?.token_type,
        };

        localStorage.setItem(env_, JSON.stringify(myToken));
        return responseToken;
      }
      if (responseToken.fault) {
        return manageGrantAccessDisable(responseToken, env);
      }
    }).catch(err => {
      return err;
    });
  }
}

/**
 * Adaptador Mínimo Viable (MVA): Transforma la respuesta plana del microservicio
 * mtpsbk al formato wrapper legacy que espera el action creator getManagePnr.
 * Solo mapea los campos necesarios para los flujos de Facturación y Reembolsos.
 */
function transformMtpsbkToMinimalLegacy(micro) {
  // Si ya tiene formato legacy (viene con _collection), retornar sin cambios
  if (micro?._collection) return micro;
  // Si es un error/incident, retornar sin transformar
  if (micro?._meta?.class === 'Incident' || micro?.faultcode) return micro;

  const passengers = (micro.passengers || []).map(pax => ({
    allTicketNumbers: pax.ticketInfo?.coupons?.map(() => pax.ticketInfo?.eticket) || [],
    displayName: `${ pax.passengerDetails?.displayFirstName || '' } ${ pax.passengerDetails?.displayLastName || '' }`.trim(),
    email: pax.passengerInfo?.emails?.[0] || '',
    firstName: pax.passengerDetails?.displayFirstName || pax.passengerDetails?.firstName || '',
    formOfPayment: micro.paymentsMethods || [],
    lastName: pax.passengerDetails?.displayLastName || pax.passengerDetails?.lastName || '',
    middleName: '',
    ticketNumber: pax.ticketInfo?.eticket || '',
  }));

  return {
    '_collection': [{
      cart: {
        travelerInfo: { '_collection': passengers },
      },
      creationDate: micro.creationDate,
      legs: undefined,
      legsOrigin: undefined,
      pnr: micro.reservationCode,
      pnrCode: micro.reservationCode,
      reservationStatus: micro.typeReservation,
      _isMinimalAdapter: true,
    }],
    '_warnings': { '_collection': [] },
  };
}

export async function getPnr(opts, authHeader, abort = false) {
  // Se comenta código para intregrar el bff
  // const isMexEnv = /^(www.)*(mex.amlab7)\.com$/gi.test(location.hostname);
  // const isPreProd = /^(www.)*(rc.amlab7)\.com$/gi.test(location.hostname);
  // const microEnv = getEnviroment() ? '-pd.aeromexico.com' : (isMexEnv || isPreProd) ? '-q.amlab7.com' : '-de.amlab7.com';

  // Respeta la variable `environment` de api-config.js: si tiene valor usa la URL
  // de ese ambiente; si es null toma el host directo de la URL del navegador.
  const endpoint = getHostReservationPnr(window.location.host);
  const isCognito = true;
  const token = getPromiseGrant(isCognito, 'MYB');

  // Extract pnr and lastName from authHeader for microservice headers
  const nameMatch = authHeader?.match(/name="([^"]+)"/);
  const pnrMatch = authHeader?.match(/reservation="([^"]+)"/);
  const ffnMatch = authHeader?.match(/ffp_number="([^"]+)"/);
  const lastName = nameMatch ? nameMatch[1] : '';
  const pnrValue = pnrMatch ? pnrMatch[1] : '';
  const ticketValue = ffnMatch ? ffnMatch[1] : '';

  return await token.then(async responseToken => {
    const grantAccess = isCognito ? responseToken?.access_token : responseToken?.grantAccess;

    const headers = {
      'Authorization': `Bearer ${ grantAccess }`,
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
      'channel': 'WEB',
      'lastName': lastName,
    };

    if (pnrValue) {
      headers['pnr'] = pnrValue;
    }

    if (ticketValue) {
      headers['ticket'] = ticketValue;
    }

    let signal = undefined;
    let controller = null;
    if (abort) {
      controller = new AbortController();
      signal = controller.signal;
      controller.abort();
    }
    return await doRequest( 
      endpoint,
      opts,
      headers,
      'get',
      null,
      'MYB',
      isCognito,
      false,
      true,
      true,
      signal,
      true
    ).then(async response => {
      // console.log('getPnr response:', response);
      // Se comenta línea para integrar bff
      // return transformMtpsbkToMinimalLegacy(response);
      // La respuesta ahora viene envuelta en { data: {...} }, se desenvuelve.
      const unwrapped =
        response != null && response.data != null
          ? response.data
          : response;
      // console.log('getPnr payload type:', typeof unwrapped, unwrapped);
      // La respuesta 200 llega cifrada (texto Base64). Se descifra con
      // decryptReservationPayload para obtener el objeto de reservación en JSON.
      // Si es un objeto (p. ej. un cuerpo de error/incidente) se usa tal cual.
      let payload = unwrapped;
      if (typeof unwrapped === 'string') {
        try {
          payload = await decryptReservationPayload(unwrapped);
          // console.log('getPnr decrypted payload:', payload);
        } catch (decryptError) {
          // console.log('getPnr decrypt error:', decryptError);
        }
      }
      return transformMtpsbkToMinimalLegacy(payload);
    }).catch(error => {
      return error;
    });
  }).catch(error => {
    return error;
  });
}

function getAmaiaBaseUrl() {
  // Detección de entornos pre-productivos específicos
  const isProd = getEnviroment();

  if (isProd) {
    return 'https://asamaia.amsrvc.com';
  }
  // Fallback a entorno de desarrollo
  return 'https://dyn-amaiaqa-ac.amlab7.com';
}

export async function streamAmaiaResponse(prompt, sessionId, context, onChunk, onError, onComplete) {
  try {
    const baseUrl = getAmaiaBaseUrl();
    const endpoint = '/aero/llm/srv-amaia/agentic/ecommerce';
    const url = `${ baseUrl }${ endpoint }`;

    // Recupera sesión de localStorage si no viene en argumentos para continuidad
    const finalSessionId = sessionId || localStorage.getItem('gist.web.sessionId');
    const payload = {
      ...context,
      prompt,
      'session_id': finalSessionId,
    };

    const response = await fetch(url, {
      body: JSON.stringify(payload),
      headers: { 'Content-Type': 'application/json' },
      method: 'POST',
    });

    if (!response.ok) {
      throw new Error(`AMAIA Service Error: ${ response.status } ${ response.statusText }`);
    }
    if (!response.body) {
      throw new Error('El navegador no soporta ReadableStream en la respuesta.');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let done = false;
    let buffer = '';
    let hasReceivedContent = false;

    while (!done) {
      const { done: readerDone, value } = await reader.read();
      done = readerDone;
      // stream: true mantiene estado interno para caracteres multibyte divididos entre chunks
      const chunk = decoder.decode(value || new Uint8Array(), { stream: !done });
      buffer += chunk;
      const lines = buffer.split('\n');
      // Preserva el último fragmento si no termina en salto de línea
      buffer = done ? '' : lines.pop();

      if (lines.length > 0) {
        for (const line of lines) {
          const trimmedLine = line.trim();
          if (trimmedLine.startsWith('data:')) {
            try {
              const jsonString = trimmedLine.substring(5).trim();
              if (!jsonString) {
                continue;
              }
              const streamPayload = JSON.parse(jsonString);
              if (streamPayload.token && onChunk) {
                hasReceivedContent = true;
                onChunk(streamPayload.token);
              }
            } catch {
              // Omite errores de parseo para garantizar continuidad del stream
            }
          }
        }
      }
    }
    if (!hasReceivedContent) {
      throw new Error('AMAIA_NO_RESULTS');
    }
    if (onComplete) onComplete();
  } catch (error) {
    if (onError) onError(error);
  }
}

// Retorna el stream sin procesar para consumo en capas superiores (ej. Sagas)
export async function fetchAmaiaStream(prompt, sessionId) {
  const baseUrl = getAmaiaBaseUrl();
  const endpoint = `${ baseUrl }/aero/llm/srv-amaia/agentic/ecommerce`;

  const response = await fetch(endpoint, {
    body: JSON.stringify({ prompt, 'session_id': sessionId, 'user_time_zone': Intl.DateTimeFormat().resolvedOptions().timeZone }),
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    },
    method: 'POST',
  });

  if (!response.ok) {
    throw new Error(`API request failed with status ${ response.status }`);
  }

  return response.body;
}

export async function getIropEligibility(opts, authHeader) {
  const endPoint = '/irops/pnrlookup/iropEligibility';
  const isCognito = isTokenCognito(endPoint);
  const headersRequest = addHeaders(opts, authHeader, null);
  return await doRequest(
    endPoint,
    opts,
    headersRequest.headers,
    'get',
    null,
    'MYB',
    isCognito
  ).then(response => {
    return response;
  }).catch(error => {
    return error;
  });
}

export async function postPrioritylist(dataToSend) {
  const endPoint = '/prioritylist';
  const isCognito = isTokenCognito(endPoint);
  const token = getPromiseGrant(isCognito, 'MYB');

  return await token.then(async responseToken => {
    const grantAccess = isCognito ? responseToken?.access_token : responseToken?.grantAccess;
    let options = {};
    options.headers = {
      'client': 'ecommerce',
      'flow': 'irop',
      'pos': 'web',
      'x-clientkey': isCognito ? getBasicTokenCognito(REACT_APP_CLIENT_COGNITO_ID) : process.env.REACT_APP_CLIENT_ID,
    };
    options = addHeaders(options, null, grantAccess);

    return await doRequest(
      endPoint,
      dataToSend,
      options.headers,
      METHOD.POST,
      dataToSend,
      'MYB',
      isCognito
    ).then(response => {
      return response;
    }).catch(error => {
      return error;
    });
  }).catch(err => {
    return err;
  });
}

export async function getEMDVoucherCompensation(opts) {
  const requestToken = getAmGrant();
  return await requestToken.then(async responseToken => {
    if (responseToken?._meta?.class === 'Incident') {
      return responseToken;
    }
    if (responseToken.fault) {
      return manageGrantAccessDisable(responseToken);
    }
    const grantAccess = responseToken.grantAccess;
    const headersRequest = addHeaders(opts, null, grantAccess);
    return await doRequest(
      '/voucher/quote/compensation?recordLocator={pnr}&only15=false&store={storeCode}&pos={posCode}&language={language}',
      opts,
      headersRequest.headers,
    ).then(response => {
      return response;
    }).catch(error => {
      return error;
    });
  }).catch(err => {
    return err;
  });
}

export async function getSeatMap(opts) {
  const endPoint = '/manage/seatmaps';
  const isCognito = isTokenCognito(endPoint);
  const token = getPromiseGrant(isCognito, 'MYB');

  return await token.then(async responseToken => {
    const grantAccess = isCognito ? responseToken?.access_token : responseToken?.grantAccess;
    const headersRequest = addHeaders(opts, null, grantAccess);
    return await doRequest(
      `${endPoint}?cartId={cartId}&store={storeCode}&pos={posCode}&upgrade={upgrade}`,
      opts,
      headersRequest.headers,
      'get',
      null,
      'MYB',
      isCognito
    ).then(response => {
      return response;
    }).catch(error => {
      return error;
    });
  }).catch(err => {
    return err;
  });
}

export async function putShopingCart(opts, pnrModel, isRedeemed = false, token = '') {
  const endPoint1 = '/manage/addBagByBenefit';
  const endPoint2 = '/manage/carts';
  const isCognito = isTokenCognito(isRedeemed ? endPoint1 : endPoint2);
  const getToken = getPromiseGrant(isCognito, 'MYB');

  pnrModel.setToken(token);
  return await getToken.then(async responseToken => {
    const grantAccess = isCognito ? responseToken?.access_token : responseToken?.grantAccess;
    opts = addHeaders(opts, null, grantAccess);
    const url = `${isRedeemed ? endPoint1 : endPoint2}/{cartId}?store={storeCode}&pos={posCode}&language={language}`;
    return await doRequest(
      url,
      opts,
      opts.headers,
      METHOD.PUT,
      pnrModel.travelerInfoToApi(),
      'MYB',
      isCognito
    ).then(response => {
      return response;
    }).catch(error => {
      return error;
    });
  }).catch(err => {
    return err;
  });
}

export async function submitPayment(opts, purchaseOrder) {
  const endPoint = '/manage/po';
  const isCognito = isTokenCognito(endPoint);
  const requestToken = getAmGrant('MYB', isCognito);
  return await requestToken.then(async responseToken => {
    if (responseToken && responseToken._meta && responseToken._meta.class === 'Incident') {
      return responseToken;
    }
    if (responseToken.fault) {
      return manageGrantAccessDisable(responseToken);
    }
    const grantAccess = isCognito ? responseToken?.access_token : responseToken?.grantAccess;
    opts = addHeaders(opts, null, grantAccess);
    return await doRequest(
      `${endPoint}?store={storeCode}&pos={posCode}&language={language}`,
      opts,
      opts.headers,
      METHOD.POST,
      purchaseOrder.toAPI(),
      'MYB',
      isCognito
    ).then(response => {
      return response;
    }).catch(error => {
      return error;
    });
  }).catch(err => {
    return err;
  });
}

export async function getAncillaries(opts) {
  const endPoint = '/manage/ancillaries';
  const isCognito = isTokenCognito(endPoint);
  const token = getPromiseGrant(isCognito, 'MYB');

  return await token.then(async responseToken => {
    const grantAccess = isCognito ? responseToken?.access_token : responseToken?.grantAccess;
    const headersRequest = addHeaders(opts, null, grantAccess);

    // el parámetro isCorporateFlow se esta recibiendo del archivo index.js de redux/actions función -> retriveAllAncillariesPNR
    return await doRequest(
      `${endPoint}?store={storeCode}&cartId={cartId}&pos={posCode}&language={language}&isCorporateFlow={isCorporateFlow}`,
      opts,
      headersRequest.headers,
      'get',
      null,
      'MYB',
      isCognito
    ).then(response => {
      return response;
    }).catch(error => {
      return error;
    });
  }).catch(err => {
    return err;
  });
}

export async function getPriceChubb(opts) {
  const endPoint = '/manage/ancillaries/chubb';
  const isCognito = isTokenCognito(endPoint);
  const token = getPromiseGrant(isCognito, 'MYB');

  return await token.then(async responseToken => {
    const grantAccess = isCognito ? responseToken?.access_token : responseToken?.grantAccess;
    const headersRequest = addHeaders(opts, null, grantAccess);
    return await doRequest(
      `${endPoint}?store={storeCode}&cartId={cartId}&pos={posCode}&stateCode={stateCode}`,
      opts,
      headersRequest.headers,
      'get',
      null,
      'MYB',
      isCognito
    ).then(response => {
      const responseSVI = response.filter(ancillary => {
        return ancillary.type === 'SVI';
      });

      return responseSVI.length > 0 ? responseSVI[0] : [];
    }).catch(error => {
      return error;
    });
  }).catch(err => {
    return err;
  });
}

export async function getFormsOfPayment(opts) {
  const endPoint = '/manage/payment-methods';
  const isCognito = isTokenCognito(endPoint);
  const token = getPromiseGrant(isCognito, 'MYB');

  return await token.then(async responseToken => {
    const grantAccess = isCognito ? responseToken?.access_token : responseToken?.grantAccess;
    const headersRequest = addHeaders(opts, null, grantAccess);
    return await doRequest(
      `${endPoint}?store={storeCode}&cartId={cartId}&pos={posCode}&isCorporateFlow={isCorporateFlow}`,
      opts,
      headersRequest.headers,
      'get',
      null,
      'MYB',
      isCognito
    ).then(response => {
      return response;
    }).catch(error => {
      return error;
    });
  }).catch(err => {
    return err;
  });
}

export async function getMealsMenu(opts) {
  const endPoint = '/manage/meal/menu';
  const isCognito = isTokenCognito(endPoint);
  const token = getPromiseGrant(isCognito, 'MYB');

  return await token.then(async responseToken => {
    const grantAccess = isCognito ? responseToken?.access_token : responseToken?.grantAccess;
    opts = addHeaders(opts, null, grantAccess);
    return await doRequest(
      `${endPoint}?segment={segment}&flightNumber={flightNumber}&store={store}`,
      opts,
      opts.headers,
      'get',
      null,
      'MYB',
      isCognito
    ).then(response => {
      return response;
    }).catch(error => {
      return error;
    });
  }).catch(err => {
    return err;
  });
}

export async function postMealsSelected(opts, mealsSelected) {
  const endPoint = '/manage/meal/select';
  const isCognito = isTokenCognito(endPoint);
  const token = getPromiseGrant(isCognito, 'MYB');

  return await token.then(async responseToken => {
    const grantAccess = isCognito ? responseToken?.access_token : responseToken?.grantAccess;
    opts = addHeaders(opts, null, grantAccess);
    return await doRequest(
      `${endPoint}/{cartId}?store={storeCode}&pos={posCode}&language={language}`,
      opts,
      opts.headers,
      METHOD.POST,
      mealsSelected,
      'MYB',
      isCognito
    ).then(response => {
      return response;
    }).catch(error => {
      return error;
    });
  }).catch(err => {
    return err;
  });
}

export async function getPreselectSeats(opts) {
  const endPoint = '/manage/seatmaps/preSelectSeat';
  const isCognito = isTokenCognito(endPoint);
  const token = getPromiseGrant(isCognito, 'MYB');

  return await token.then(async responseToken => {
    const grantAccess = isCognito ? responseToken?.access_token : responseToken?.grantAccess;
    opts = addHeaders(opts, null, grantAccess);
    return await doRequest(
      `${endPoint}?cartId={cartId}&store={storeCode}&pos={posCode}`,
      opts,
      opts.headers,
      'get',
      null,
      'MYB',
      isCognito
    ).then(response => {
      return response;
    }).catch(error => {
      return error;
    });
  }).catch(err => {
    return err;
  });
}

export async function putFFNumberMicro(dataFFNumber, opts) {
  const endPoint = '/ffn-association';
  const isCognito = isTokenCognito(endPoint);
  const token = getPromiseGrant(isCognito, 'MYB');

  return await token.then(async responseToken => {
    const grantAccess = isCognito ? responseToken?.access_token : responseToken?.grantAccess;
    opts = addHeaders(opts, null, grantAccess, null);
    const data = transformAssociationFFNumber.toAPIModel(dataFFNumber);

    return await doRequest(
      endPoint,
      opts,
      opts.headers,
      METHOD.PUT,
      data,
      'MYB',
      isCognito
    ).then(response => {
      return response;
    }).catch(error => {
      return error;
    });
  }).catch(err => {
    return err;
  });
}

export async function changeItinerary(opts) {
  const endPoint = '/manage/change-status';
  const isCognito = isTokenCognito(endPoint);
  const token = getPromiseGrant(isCognito, 'MYB');

  return await token.then(async responseToken => {
    const grantAccess = isCognito ? responseToken?.access_token : responseToken?.grantAccess;
    const headersRequest = addHeaders(opts, null, grantAccess);

    return await doRequest(
      endPoint,
      opts,
      headersRequest.headers,
      METHOD.POST,
      opts,
      'MYB',
      isCognito
    ).then(response => {
      return response;
    }).catch(error => {
      return error;
    });
  }).catch(err => {
    return err;
  });
}

export async function deleteSession({ accountNumber, token }) {
  const isMexEnv = /^(www.)*(mex.amlab7)\.com$/gi.test(location.hostname);
  const isPreProd = /^(www.)*(rc.amlab7)\.com$/gi.test(location.hostname);
  const isPreProdNew = /^(www.)*(new-rc.amlab7)\.com$/gi.test(location.hostname);
  const microEnv = getEnviroment() ? '-pd.aeromexico.com' : (isMexEnv || isPreProd || isPreProdNew) ? '-q.amlab7.com' : '-de.amlab7.com';
  const endpoint = `${POD_CODES.poflbk}${microEnv}/uc/login`;
  const isCognito = false;
  const opts = {
    headers: {
      'pos': 'Web',
      'flow': 'Profile',
      accountNumber,
      token,
    },
  };

  return await doRequest(
    endpoint,
    opts,
    opts.headers,
    METHOD.DELETE,
    null,
    'MYB',
    isCognito,
    false,
    true,
  ).then(response => {
    return response;
  }).catch(error => {
    return error;
  });
}

export async function userProfileSignUp(formData) {
  const dataToSend = {
    data: formData,
  };

  const isMexEnv = /^(www.)*(mex.amlab7)\.com$/gi.test(location.hostname);
  const isPreProd = /^(www.)*(rc.amlab7)\.com$/gi.test(location.hostname);

  const microEnv = getEnviroment() ? '-pd.aeromexico.com' : (isMexEnv || isPreProd) ? '-q.amlab7.com' : '-de.amlab7.com';
  const endpoint = `${POD_CODES.poflbk}${microEnv}/uc/enrollment/enroll`;
  const isCognito = false;
  const opts = {
    headers: {
      'pos': 'Web',
      'flow': 'Profile',
    },
  };

  return await doRequest(
    endpoint,
    opts,
    opts.headers,
    METHOD.POST,
    dataToSend,
    'MYB',
    isCognito,
    false,
    true,
  ).then(response => {
    return response;
  }).catch(error => {
    return error;
  });
}

export async function userProfileSendOtp(data) {
  const sendOtp = true;
  const {
    email,
    name: memberName,
  } = data;

  const isMexEnv = /^(www.)*(mex.amlab7)\.com$/gi.test(location.hostname);
  const isPreProd = /^(www.)*(rc.amlab7)\.com$/gi.test(location.hostname);
  const isPreProdNew = /^(www.)*(new-rc.amlab7)\.com$/gi.test(location.hostname);
  const microEnv = getEnviroment() ? '-pd.aeromexico.com' : (isMexEnv || isPreProd || isPreProdNew) ? '-q.amlab7.com' : '-de.amlab7.com';
  const endpoint = `${POD_CODES.poflbk}${microEnv}/uc/enrollment/enroll?email=${email}&sendOtp=${sendOtp}&memberName=${memberName}`;
  const isCognito = false;
  const opts = {
    headers: {
      'pos': 'Web',
      'flow': 'Profile',
    },
  };

  return await doRequest(
    endpoint,
    opts,
    opts.headers,
    METHOD.GET,
    data, // data
    'MYB',
    isCognito,
    false,
    true,
  ).then(response => {
    return response;
  }).catch(error => {
    return error;
  });
}

export async function userProfileSignIn(opts) {
  const endPoint = '/profile/user-profile-sign-in';
  const isCognito = isTokenCognito(endPoint);
  const token = getPromiseGrant(isCognito, 'PRO');

  return await token.then(async responseToken => {
    const grantAccess = isCognito ? responseToken?.access_token : responseToken?.grantAccess;
    const headersRequest = addHeaders(opts, null, grantAccess);
    return await doRequest(
      endPoint,
      opts,
      headersRequest.headers,
      METHOD.POST,
      opts,
      'MYB',
      isCognito
    ).then(response => {
      return response;
    }).catch(error => {
      return error;
    });
  }).catch(err => {
    return err;
  });
}

export async function userProfileSignOut(opts) {
  const endPoint = '/profile/user-profile-sign-out';
  const isCognito = isTokenCognito(endPoint);
  const token = getPromiseGrant(isCognito, 'PRO');

  return await token.then(async responseToken => {
    const grantAccess = isCognito ? responseToken?.access_token : responseToken?.grantAccess;
    const headersRequest = addHeaders(opts, null, grantAccess);
    return await doRequest(
      endPoint,
      opts,
      headersRequest.headers,
      METHOD.POST,
      opts,
      'MYB',
      isCognito
    ).then(response => {
      return response;
    }).catch(error => {
      return error;
    });
  }).catch(err => {
    return err;
  });
}

export async function retrieveProfileUserSession(opts) {
  const isValidSesion = true;
  const endPoint = '/profile/user-profile-trips';
  const isCognito = isTokenCognito(endPoint);
  const token = getPromiseGrant(isCognito, 'PRO');

  return await token.then(async responseToken => {
    const grantAccess = isCognito ? responseToken?.access_token : responseToken?.grantAccess;
    const headersRequest = addHeaders(opts, null, grantAccess, isValidSesion);

    return await doRequest(
      endPoint,
      opts,
      headersRequest.headers,
      METHOD.GET,
      opts,
      'MYB',
      isCognito
    ).then(response => {
      return response;
    }).catch(error => {
      return error;
    });
  }).catch(err => {
    return err;
  });
}

export async function retrieveUserCampingEnrolled(data) {
  const url = `${HOSTV4}/clubPremier/campaign/member-enrolled`;

  const opts = {
    'Access-Control-Allow-Origin': '*',
    'Content-Type': 'application/json',
  };

  return await fetch(url, {
    body: JSON.stringify(data),
    headers: opts,
    method: 'POST',
  }).then(response => response.json())
    .then(data => {
      if (data.responseStatus === 404) {
        return {
          errorCode: '404',
          errorDescription: '',
          isSuccess: false,
          result: false,
        };
      } else {
        return {
          errorCode: data.errorCode,
          errorDescription: data.errorDescription || data.description,
          errors: data.errors,
          isSuccess: data.result !== undefined,
          result: data.result,
        };
      }
    }).catch((error) => {
      return error;
    });
}

export async function userProfileIsValidCPSession(opts) {
  const isValidSesion = true;
  const endPoint = '/profile/user-profile-valid-session';
  const isCognito = isTokenCognito(endPoint);
  const token = getPromiseGrant(isCognito, 'PRO');

  return await token.then(async responseToken => {
    const grantAccess = isCognito ? responseToken?.access_token : responseToken?.grantAccess;
    const headersRequest = addHeaders(opts, null, grantAccess, isValidSesion);
    return await doRequest(
      endPoint,
      opts,
      headersRequest.headers,
      METHOD.GET,
      opts,
      'MYB',
      isCognito
    ).then(response => {
      return response;
    }).catch(error => {
      return error;
    });
  }).catch(err => {
    return err;
  });
}

export async function updateConsentCp(data, opts) {
  const endPoint = '/clubPremier/update-consent';
  const isCognito = isTokenCognito(endPoint);
  const token = getPromiseGrant(isCognito, 'PRO');

  return await token.then(async responseToken => {
    const grantAccess = isCognito ? responseToken?.access_token : responseToken?.grantAccess;
    opts = addHeaders(opts, null, grantAccess, null);

    return await doRequest(
      endPoint,
      opts,
      opts.headers,
      METHOD.PUT,
      data,
      'MYB',
      isCognito
    ).then(response => {
      return response;
    }).catch(error => {
      return error;
    });
  }).catch(err => {
    return err;
  });
}

export async function sendAncillaryRejectEvent(data) {
  const headersMC = getHeaderMarketingCloud(REACT_APP_INTERACTION_STUDIO_API_TOKEN, REACT_APP_INTERACTION_STUDIO_API_SECRET, REACT_APP_INTERACTION_STUDIO_API_URL);
  const authorization = `${headersMC.tokenSecret}:${headersMC.keySecret}`;
  const opts = {
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Basic ${btoa(authorization)}`,
    },
  };
  const url = `${HOSTV1}/aerovias/api2/authevent/${headersMC.envSecret}`;

  const requestObj = {
    body: JSON.stringify(data),
    headers: opts.headers,
    method: 'POST',
  };
  if (headersMC.keySecret && headersMC.tokenSecret && headersMC.envSecret) {
    return await fetch(url, requestObj).catch(error => error);
  }
}

function addHeaders(opts, authHeader, grantAccess = '', isValidSesion) {
  // Leer numberOfDecimalsBE del estado (proviene del servicio th-web-validations).
  // Solo se propaga a los microservicios si la bandera realmente llegó.
  const currentState = store.getState();
  const numberOfDecimalsBE = currentState?.Manage?.stateStoreFront?.numberOfDecimalsBE;
  const hasNumberOfDecimalsBE = numberOfDecimalsBE != null;

  // Dejar el header de 'authorization', cuando se este seguro que V1 ha dejado de operar en MYB 2.0.
  if (opts?.pos?.isCorporateFlow) {
    const { token, pos } = opts;
    const params = {
      ...token,
      headers: {
        ...pos?.headers,
        'authorization': 'Atmosphere realm=http://atmosphere,atmosphere_app_id=WorkAndCoApp, atmosphere_signature_method=NONE',
        'Strict-Transport-Security': 'max-age=300; includeSubDomains',
        'Access-Control-Allow-Origin': '*',
        'access_type': 'client_credentials',
        'Content-Type': 'application/json',
        'cache-control': 'no-cache',
        'accept': 'application/json, text/plain, */*',
        'referrerPolicy': 'no-referrer',
        'pos': 'WEB',
        'isCorporateFlow': pos?.isCorporateFlow,
        'corporateName': pos?.corporateName ? pos?.corporateName : 'nutravel',
        ...(hasNumberOfDecimalsBE && { 'numberOfDecimalsBE': String(numberOfDecimalsBE) }),
      },
    };

    if (authHeader) {
      params.headers['X-AM-User-Auth'] = authHeader;
    }

    if (grantAccess !== '') {
      params.headers['Authorization'] = `Bearer ${grantAccess}`;
      delete params.headers.authorization;
    }

    if (isValidSesion) {
      params.headers['user-token'] = token;
    }
    return params;
  }

  opts = opts || {};
  const params = {
    ...opts,
    headers: {
      ...opts.headers,
      'authorization': 'Atmosphere realm=http://atmosphere,atmosphere_app_id=WorkAndCoApp, atmosphere_signature_method=NONE',
      'Strict-Transport-Security': 'max-age=300; includeSubDomains',
      'Access-Control-Allow-Origin': '*',
      'access_type': 'client_credentials',
      'Content-Type': 'application/json',
      'cache-control': 'no-cache',
      'accept': 'application/json, text/plain, */*',
      'referrerPolicy': 'no-referrer',
      'pos': 'WEB',
      ...(hasNumberOfDecimalsBE && { 'numberOfDecimalsBE': String(numberOfDecimalsBE) }),
    },
  };

  if (authHeader) {
    params.headers['X-AM-User-Auth'] = authHeader;
  }

  if (grantAccess !== '') {
    params.headers['Authorization'] = `Bearer ${grantAccess}`;
    delete params.headers.authorization;
  }

  if (isValidSesion) {
    params.headers['user-token'] = opts;
  }

  return params;
}

export async function sendEmailItineraryPnrRequest(storefront, language, email, lastName, reservation, cartId) {
  const endPoint = '/manage/email-receipt';
  const isCognito = isTokenCognito(endPoint);
  const token = getPromiseGrant(isCognito, 'MYB');

  return await token.then(async responseToken => {
    const grantAccess = isCognito ? responseToken?.access_token : responseToken?.grantAccess;
    let options = {
      storeCode: storefront.split('-')[1],
      language: language,
      posCode: 'WEB',
      email: email,
      pnr: reservation,
      cartId: cartId,
      jsonExpected: false,
    };
    options.headers = {
      'X-AM-User-Auth': `PNR name=${lastName},reservation=${reservation}`,
      'Content-Type': 'application/json',
    };
    options = addHeaders(options, null, grantAccess);
    return await doRequest(
      `${endPoint}?store={storeCode}&pos={posCode}&language={language}&email={email}&pnr={pnr}&cartId={cartId}`,
      options,
      options.headers,
      METHOD.POST,
      null,
      'MYB',
      isCognito
    ).then(response => {
      return response;
    }).catch(error => {
      return error;
    });
  }).catch(err => {
    return err;
  });
}

export async function submitValidateEmdVoucher(opts, dataToSend) {
  const endPoint = '/manage/emdVoucher/validate';
  const isCognito = isTokenCognito(endPoint);
  const token = getPromiseGrant(isCognito, 'MYB');

  return await token.then(async responseToken => {
    const grantAccess = isCognito ? responseToken?.access_token : responseToken?.grantAccess;
    opts = addHeaders(opts, null, grantAccess);
    return await doRequest(
      `${endPoint}?store={storeCode}&pos={posCode}&language={language}`,
      opts,
      opts.headers,
      METHOD.POST,
      dataToSend,
      'MYB',
      isCognito
    ).then(response => {
      return response;
    }).catch(error => {
      return error;
    });
  }).catch(err => {
    return err;
  });
}

export async function submitRemoveEmdVoucher(opts) {
  const endPoint = '/manage/emdVoucher/remove';
  const isCognito = isTokenCognito(endPoint);
  const token = getPromiseGrant(isCognito, 'MYB');

  return await token.then(async responseToken => {
    const grantAccess = isCognito ? responseToken?.access_token : responseToken?.grantAccess;
    opts = addHeaders(opts, null, grantAccess);
    return await doRequest(
      `${endPoint}/{cartId}?store={storeCode}&pos={posCode}&language={language}`,
      opts,
      opts.headers,
      METHOD.DELETE,
      null,
      'MYB',
      isCognito
    ).then(response => {
      return response;
    }).catch(error => {
      return error;
    });
  }).catch(err => {
    return err;
  });
}

export async function getAmGrant(typeToken = 'MYB', isCognito = false) {
  const isBkn = typeToken !== 'MYB';
  const data = {
    'client_id': isCognito ? getBasicTokenCognito(REACT_APP_CLIENT_COGNITO_ID) : process.env.REACT_APP_CLIENT_ID,
    'client_secret': isCognito ? getBasicTokenCognito(REACT_APP_CLIENT_COGNITO_SECRET) : process.env.REACT_APP_CLIENT_SECRET,
  };

  let token = '';
  if (isCognito) {
    token = isBkn ? getBasicTokenCognito(REACT_APP_TOKEN_AUTH_COGNITO_BKN) : getBasicTokenCognito(REACT_APP_TOKEN_AUTH_COGNITO);
  } else {
    token = isBkn ? process.env['REACT_APP_TOKEN_AUTH_BKN'] : process.env['REACT_APP_TOKEN_AUTH'];
  }

  let headers = {};

  const headersGrantCognito = {
    'Authorization': `Basic ${token}`,
    'Content-Type': 'application/x-www-form-urlencoded',
  };

  const headersGrant = {
    'cache-control': 'no-cache',
    'access_type': 'client_credentials',
  };

  let url = '';

  if (!isCognito) {
    headers = Object.assign(headersGrantCognito, headersGrant);
    url = '/am-grant/grantAccess';
  } else {
    headers = headersGrantCognito;
    url = '/oauth2/token?grant_type=client_credentials';
  }

  const response = await doRequest(url, {}, headers, isCognito ? METHOD.POST : METHOD.GET, data, typeToken, isCognito);
  return response;
}

export async function getSessionStatus(body) {
  const endPoint = '/validate/session';
  const isCognito = isTokenCognito(endPoint);
  const token = getPromiseGrant(isCognito, 'MYB');

  return await token.then(async responseToken => {
    const grantAccess = isCognito ? responseToken?.access_token : responseToken?.grantAccess;

    const headers = {
      headers: {
        client: 'ecommerce',
        flow: 'myb',
        pos: 'web',
        'x-clientkey': isCognito ? getBasicTokenCognito(REACT_APP_CLIENT_COGNITO_ID) : process.env.REACT_APP_CLIENT_ID,
      },
    };
    const opts = addHeaders(headers, null, grantAccess);

    return await doRequest(
      endPoint,
      opts,
      opts.headers,
      METHOD.POST,
      body,
      'MYB',
      isCognito
    )
      .then(response => {
        return response;
      })
      .catch(error => {
        return error;
      });
  }).catch(err => {
    return err;
  });
}

export async function updateCartId(cartId, storefront) {
  const endPoint = '/manage/updateCartId';
  const isCognito = isTokenCognito(endPoint);
  const token = getPromiseGrant(isCognito, 'MYB');

  return await token.then(async responseToken => {
    const grantAccess = isCognito ? responseToken?.access_token : responseToken?.grantAccess;
    const request = {};
    const opts = {
      storeCode: storefront,
      posCode: 'WEB',
      cartId: cartId,
    };
    const headersRequest = addHeaders(opts, null, grantAccess);
    return await doRequest(
      `${endPoint}/{cartId}`,
      opts,
      headersRequest.headers,
      METHOD.PUT,
      request,
      'MYB',
      isCognito
    )
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error;
      });
  }).catch((err) => {
    return err;
  });
}

export async function getBearerTokenContactability() {
  const credentials = {
    apiName: REACT_APP_CONTACTABILITY_ID,
    secret: REACT_APP_CONTACTABILITY_SECRET,
  };
  const endPoint = '/contactability/token';
  const isCognito = isTokenCognito(endPoint);
  const token = getPromiseGrant(isCognito, 'MYB');
  return await token.then(async(responseToken) => {
    if (responseToken?._meta?.class === 'Incident') {
      return responseToken;
    }
    if (responseToken?.fault) {
      return manageGrantAccessDisable(responseToken);
    }
    const grantAccess = isCognito ? responseToken?.access_token : responseToken?.grantAccess;
    const request = credentials;
    const opts = {};
    const headersRequest = addHeaders(opts, null, grantAccess);
    return await doRequest(
      `${endPoint}`,
      opts,
      headersRequest.headers,
      METHOD.POST,
      request,
    )
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error;
      });
  }).catch((err) => {
    return err;
  });
}

export async function saveContactabilityDigital(data, token) {
  const endPoint = '/contactability/digital';
  const isCognito = isTokenCognito(endPoint);
  const requestToken = getPromiseGrant(isCognito, 'MYB');
  return await requestToken.then(async(responseToken) => {
    if (responseToken?._meta?.class === 'Incident') {
      return responseToken;
    }
    if (responseToken?.fault) {
      return manageGrantAccessDisable(responseToken);
    }
    const grantAccess = isCognito ? responseToken?.access_token : responseToken?.grantAccess;
    const request = data;
    const opts = {};
    const headersRequest = addHeaders(opts, null, grantAccess);
    headersRequest.headers['Authorization'] = `Bearer ${token}`;
    return await doRequest(
      `${endPoint}`,
      opts,
      headersRequest.headers,
      METHOD.POST,
      request,
    )
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error;
      });
  }).catch((err) => {
    return err;
  });
}

export async function getFulfillRefundData(email, name, _, dispatch, documentNumbers, refundForCode96Dot5) {
  const { fulfillRefund } = getRefundEndpoints();

  const headers = buildRefundHeaders({});

  const etickets = documentNumbers.length === 0
    ? []
    : documentNumbers.map(doc => ({ 'eticket': doc.documentNumber || doc }));

  const request = {
    'refundDriver': {
      'applicationMode': 'SelfService',
      'actionType': 'FulfillRefund',
      'applicationClient': 'EComm',
    },
    'refundCase': {
      'etickets': etickets,
      'reservationCode': '',
      'taxRefund': `${ refundForCode96Dot5 }`,
      'reinstallPoints': 'false',
      'sfRecordId': '',
      'applyRefundTo': 'FOP',
      'passengerName': name,
      'eMail': email || '',
    },
    'reinstallFeeInfo': {
      'reinstallFeeAmntApplied': 'false',
      'reinstallFeePaymentAuthorization': '',
      'reinstallFeePaymentAmount': '',
      'reinstallFeePaymentCard': '',
      'reinstallFeeTicketNumber': '',
    },
  };

  return await doRequest(
    fulfillRefund,
    {},
    headers,
    METHOD.POST,
    request,
    'MYB',
    false,
    true
  )
    .then((response) => {
      return response;
    })
    .catch((error) => {
      return error;
    });
}

export async function getQuoteRefundData(email, name, pnr) {
  const { quoteRefund } = getRefundEndpoints();

  const headers = buildRefundHeaders({});

  const request = {
    'driver': {
      'applicationMode': 'SelfService',
      'actionType': 'QuoteRefund',
      'applicationClient': 'EComm',
    },
    'quoteCase': {
      'eticket': '',
      'reservationCode': pnr,
      'sfRecordID': '0',
      'passengerName': name,
      'email': email || '',
    },
  };

  return await doRequest(
    quoteRefund,
    {},
    headers,
    METHOD.POST,
    request,
    'MYB',
    false,
    true
  )
    .then((response) => {
      return response;
    })
    .catch((error) => {
      return error;
    });
}

export async function userProfileRegistrationEmailValidation(email) {
  const isMexEnv = /^(www.)*(mex.amlab7)\.com$/gi.test(location.hostname);
  const isPreProd = /^(www.)*(rc.amlab7)\.com$/gi.test(location.hostname);
  const isPreProdNew = /^(www.)*(new-rc.amlab7)\.com$/gi.test(location.hostname);
  const microEnv = getEnviroment() ? '-pd.aeromexico.com' : (isMexEnv || isPreProd || isPreProdNew) ? '-q.amlab7.com' : '-de.amlab7.com';
  const endpoint = `${POD_CODES.poflbk}${microEnv}/uc/enrollment/enroll?email=${email}`;
  const isCognito = false;
  const opts = {
    headers: {
      'pos': 'Web',
      'flow': 'Profile',
    },
  };

  return await doRequest(
    endpoint,
    opts,
    opts.headers,
    METHOD.GET,
    null, // data
    'MYB',
    isCognito,
    false,
    true,
  ).then(response => {
    return response;
  }).catch(error => {
    return error;
  });
}

export async function userProfileRegistrationTutorValidation(data) {
  const { email, guardianAccount, birthdayDate } = data;
  const isMexEnv = /^(www.)*(mex.amlab7)\.com$/gi.test(location.hostname);
  const isPreProd = /^(www.)*(rc.amlab7)\.com$/gi.test(location.hostname);
  const isPreProdNew = /^(www.)*(new-rc.amlab7)\.com$/gi.test(location.hostname);
  const microEnv = getEnviroment() ? '-pd.aeromexico.com' : (isMexEnv || isPreProd || isPreProdNew) ? '-q.amlab7.com' : '-de.amlab7.com';
  const endpoint = `${POD_CODES.poflbk}${microEnv}/uc/enrollment/enroll?email=${email}&guardianAccount=${guardianAccount}&dateOfBirth=${birthdayDate}`;
  const isCognito = false;

  const opts = {
    headers: {
      'pos': 'Web',
      'flow': 'Profile',
    },
  };

  return await doRequest(
    endpoint,
    opts,
    opts.headers,
    METHOD.GET,
    null, // data
    'MYB',
    isCognito,
    false,
    true,
    false,
  ).then(response => {
    return response;
  }).catch(error => {
    return error;
  });
}

export function fetchHostname() {
  const env = window.location.hostname;
  const isBetaProd = /^((www|beta|alpha).)*(aeromexico)\.com$/gi.test(env);
  const isCertEnv = /(amlab7)\.com$/gi.test(env);
  return isBetaProd ? 'prod' : isCertEnv ? 'cert' : 'dev';
}

export function fetchHostnameLogin() {
  const isProd = getEnviroment();
  return isProd ? 'prod' : 'cert';
}

export async function getPromiseRefound(dispatch) {
  const env_ = 'REFUND_ACCESS_TOKEN';
  const tokenStorage = JSON.parse(sessionStorage.getItem(env_));

  const now = new Date().getTime();
  const expirationDate = tokenStorage?.expiration_date ? new Date(tokenStorage?.expiration_date).getTime() : now;
  if (tokenStorage?.env === env_ && now < expirationDate) {
    return await tokenStorage;
  } else {
    const requestToken = getRefoundToken();
    return await requestToken.then(async responseToken => {
      if (requestToken?._result?.response?.status >= 400 || responseToken?.loginError) {
        return { ...responseToken, loginError: true };
      }

      const milliseconds = (responseToken?.Expires || responseToken?.expires) * 1000;
      const expiration = new Date(now + milliseconds);

      if (responseToken) {
        const myToken = {
          'env': env_,
          'status': responseToken?.Status || responseToken?.status,
          'error_detail': responseToken?.ErrorDetail || responseToken?.errorDetail,
          'expires': responseToken?.Expires || responseToken?.expires,
          'access': responseToken?.Access || responseToken?.access,
          'expiration_date': expiration,
        };
        sessionStorage.setItem(env_, JSON.stringify(myToken));
        dispatch(generateJwtToken(JSON.stringify(myToken)));

        return responseToken;
      }
    }).catch(err => {
      return { message: err.message, loginError: true };
    });
  }
}

/**
 * Genera un identificador único de transacción.
 * @returns {string} ID de transacción único, 1-128 caracteres
 */
function generateTransactionId() {
  return `${ Date.now() }-${ Math.random().toString(36).substring(2, 15) }`;
}

/**
 * Construye los headers obligatorios para peticiones de reembolso a través del proxy.
 * @param {object} existingHeaders - Headers ya definidos por la función llamante
 * @returns {object} Headers completos con los obligatorios garantizados
 */
function buildRefundHeaders(existingHeaders) {
  // En el flujo de reembolsos NO se propaga numberOfDecimalsBE al backend:
  // el manejo de decimales es exclusivamente de UI (el fallback a 0 vive en los
  // componentes de reembolso). Por eso este header ya no se envía.
  return {
    ...existingHeaders,
    'Content-Type': 'application/json',
    'channel': 'WEB',
    'x-transactionId': generateTransactionId(),
  };
}

export async function getRefoundToken() {
  let token = atob(REACT_APP_TOKEN_RAM);
  token = token.replace(/(\w+)(?=:)/g, '"$1"');
  const apiId = atob(REACT_APP_REFUND_API_ID);

  const formatedToken = JSON.parse(token);
  const formatedApiId = JSON.parse(apiId);

  const hostLogin = fetchHostnameLogin();
  const host = fetchHostname();

  const data = {
    'body': formatedToken[hostLogin],
  };

  const { loginRefund } = getRefundEndpoints();

  const headers = buildRefundHeaders({
    'Client-Origin': 'amcom',
    'x-apigw-api-id': formatedApiId[host],
  });

  try {
    const response = await axios.post(loginRefund, JSON.stringify(data), { headers });
    return response.data;
  } catch (error) {
    return { ...error, loginError: true };
  }
}

export function dateDiff(fec1, fec2) {
  const resul = (fec1.getTime() - fec2.getTime()) / 1000 / 60;
  return Math.round(resul);
}

export async function retrieveUpgradeVoucher(data) {
  const {
    accountNumber,
    token,
  } = data;

  const isMexEnv = /^(www.)*(mex.amlab7)\.com$/gi.test(location.hostname);
  const isPreProd = /^(www.)*(rc.amlab7)\.com$/gi.test(location.hostname);
  const isPreProdNew = /^(www.)*(new-rc.amlab7)\.com$/gi.test(location.hostname);
  const microEnv = getEnviroment() ? '-pd.aeromexico.com' : (isMexEnv || isPreProd || isPreProdNew) ? '-q.amlab7.com' : '-de.amlab7.com';
  const endpoint = `${POD_CODES.lyltbk}${microEnv}/uc/upgrade-voucher`;
  const isCognito = false;
  const opts = {
    accountNumber,
    headers: {
      'flow': 'Profile',
      'pos': 'Web',
      token,
    },
  };

  return await doRequest(
    `${endpoint}?accountNumber={accountNumber}`,
    opts,
    opts.headers,
    METHOD.GET,
    {},
    'MYB',
    isCognito,
    false,
    true,
  ).then(response => {
    return response;
  }).catch(error => {
    return error;
  });
}

export async function submitUpgradeVouchers(data) {
  const {
    payload,
    token,
  } = data;

  const isMexEnv = /^(www.)*(mex.amlab7)\.com$/gi.test(location.hostname);
  const isPreProd = /^(www.)*(rc.amlab7)\.com$/gi.test(location.hostname);
  const isPreProdNew = /^(www.)*(new-rc.amlab7)\.com$/gi.test(location.hostname);
  const microEnv = getEnviroment() ? '-pd.aeromexico.com' : (isMexEnv || isPreProd || isPreProdNew) ? '-q.amlab7.com' : '-de.amlab7.com';
  const endpoint = `${POD_CODES.lyltbk}${microEnv}/uc/upgrade-voucher`;
  const isCognito = false;
  const opts = {
    headers: {
      'flow': 'Profile',
      'pos': 'Web',
      token,
    },
  };

  return await doRequest(
    endpoint,
    opts,
    opts.headers,
    METHOD.POST,
    payload,
    'MYB',
    isCognito,
    false,
    true,
  ).then(response => {
    return response;
  }).catch(error => {
    return error;
  });
}

export async function getUserSessionTM() {
  const endPoint = '/clubPremier/sessionTM';
  const isCognito = isTokenCognito(endPoint);
  const headers = {
    'pos': 'Web',
    'flow': 'Profile',
  };

  return await doRequest(
    endPoint,
    {},
    headers,
    'get',
    null,
    'MYB',
    isCognito,
  ).then(response => {
    return response;
  }).catch(error => {
    return error;
  });
}


export async function submitUserResendOTPLogin(data) {
  const endPoint = '/app/clubPremier/resend-otp';
  const isCognito = isTokenCognito(endPoint);
  const headers = {
    'pos': 'Web',
    'flow': 'Profile',
  };

  return await doRequest(
    endPoint,
    {},
    headers,
    'post',
    data,
    'MYB',
    isCognito,
  ).then(response => {
    return response;
  }).catch(error => {
    return error;
  });
}

export async function sendRefundInfoToSalesforceApi(refundInfo) {
  const { specialRefund } = getRefundEndpoints();

  let tokenData = atob(REACT_APP_UNHANDLED_REFUND);
  tokenData = tokenData.replace(/(\w+)(?=:)/g, '"$1"');
  const formatedToken = JSON.parse(tokenData);
  const host = fetchHostname();

  const headers = buildRefundHeaders({
    'client_id': formatedToken[host].client_id,
    'client_secret': formatedToken[host].client_secret,
  });

  const quotations = refundInfo?.quoteRefundData?.quotations || [];

  const tickets = quotations.map(quotation => {
    const passenger = quotation?.passenger || {};
    const nameParts = (passenger?.name || '').split('/');

    return {
      'eticket': quotation?.documentNumber || '',
      'name': nameParts[1] || '',
      'lastName': nameParts[0] || '',
      'phone': '',
      // El pasajero puede no traer email propio; se usa el email de contacto del PNR como respaldo
      'email': passenger?.email || refundInfo?.pnrEmail || '',
    };
  });

  const data = {
    'CuentaPremier': refundInfo?.quoteRefundData?.cuentaPremier || '',
    'reservationCode': refundInfo?.fulfillRefundData?.recordLocator || '',
    'tickets': tickets,
    'boletoPremiaType': refundInfo?.quoteRefundData?.clubPremier?.boletoPremiaType || '',
  };

  return await doRequest(
    specialRefund,
    {},
    headers,
    METHOD.POST,
    data,
    'MYB',
    false,
    true
  ).then(response => {
    return response;
  }).catch(error => {
    return error;
  });
}


/**
 * Function to recover password, nip and enrollment
 * @param {*} data Reuest body payload
 * @returns
 */
export async function otpSettings(data) {
  const isMexEnv = /^(www.)*(mex.amlab7)\.com$/gi.test(location.hostname);
  const isPreProd = /^(www.)*(rc.amlab7)\.com$/gi.test(location.hostname);
  const isPreProdNew = /^(www.)*(new-rc.amlab7)\.com$/gi.test(location.hostname);
  const microEnv = getEnviroment() ? '-pd.aeromexico.com' : (isMexEnv || isPreProd || isPreProdNew) ? '-q.amlab7.com' : '-de.amlab7.com';
  const endpoint = `${POD_CODES.poflbk}${microEnv}/uc/otp`;
  const isCognito = false;
  const opts = {
    headers: {
      'pos': 'Web',
      'flow': 'Profile',
    },
  };

  return await doRequest(
    endpoint,
    opts,
    opts.headers,
    METHOD.POST,
    data,
    'MYB',
    isCognito,
    false,
    true,
  ).then(response => {
    return response;
  }).catch(error => {
    return error;
  });
}

export async function ffnAssociation(data) {
  const isMexEnv = /^(www.)*(mex.amlab7)\.com$/gi.test(location.hostname);
  const isPreProd = /^(www.)*(rc.amlab7)\.com$/gi.test(location.hostname);
  const isPreProdNew = /^(www.)*(new-rc.amlab7)\.com$/gi.test(location.hostname);
  const microEnv = getEnviroment() ? '-pd.aeromexico.com' : (isMexEnv || isPreProd || isPreProdNew) ? '-q.amlab7.com' : '-de.amlab7.com';
  const fullEndpoint = `${POD_CODES.poflbk}${microEnv}/ub/ffnassociation`;

  const opts = {
    headers: {
      'pos': 'Web',
      'flow': 'Profile',
      token: getBasicTokenCognito(REACT_APP_CLIENT_COGNITO_ID),
    },
  };

  const isCognito = false;

  return await doRequest(
    fullEndpoint,
    opts,
    opts.headers,
    METHOD.POST,
    data,
    'MYB',
    isCognito,
    false,
    true,
  ).then(response => {
    return response;
  }).catch(error => {
    return error;
  });
}

export async function getBenefitsCoreApi(data) {
  const isMexEnv = /^(www.)*(mex.amlab7)\.com$/gi.test(location.hostname);
  const isPreProd = /^(www.)*(rc.amlab7)\.com$/gi.test(location.hostname);
  const isPreProdNew = /^(www.)*(new-rc.amlab7)\.com$/gi.test(location.hostname);
  const microEnv = getEnviroment() ? '-pd.aeromexico.com' : (isMexEnv || isPreProd || isPreProdNew) ? '-q.amlab7.com' : '-de.amlab7.com';

  const opts = {
    headers: {
      'pos': 'Web',
      'flow': 'Profile',
      token: getBasicTokenCognito(REACT_APP_CLIENT_COGNITO_ID),
    },
  };
  const endpoint = `${POD_CODES.poflbk}${microEnv}/uc/benefitscore/vn`;
  const isCognito = false;

  return await doRequest(
    endpoint,
    opts,
    opts.headers,
    METHOD.POST,
    data,
    'MYB',
    isCognito,
    false,
    true,
  ).then(response => {
    return response;
  }).catch(error => {
    return error;
  });
}

export async function getBalance(accountNumber, token) {
  const isMexEnv = /^(www.)*(mex.amlab7)\.com$/gi.test(location.hostname);
  const isPreProd = /^(www.)*(rc.amlab7)\.com$/gi.test(location.hostname);
  const isPreProdNew = /^(www.)*(new-rc.amlab7)\.com$/gi.test(location.hostname);
  const microEnv = getEnviroment() ? '-pd.aeromexico.com' : (isMexEnv || isPreProd || isPreProdNew) ? '-q.amlab7.com' : '-de.amlab7.com';

  const opts = {
    headers: {
      'pos': 'Web',
      'flow': 'Profile',
      token,
    },
  };
  const endpoint = `${POD_CODES.lyltbk}${microEnv}/uc/balance/pointsummary?accountNumber=${accountNumber}`;
  const isCognito = false;

  return await doRequest(
    endpoint,
    opts,
    opts.headers,
    METHOD.GET,
    null,
    'MYB',
    isCognito,
    false,
    true,
  ).then(response => {
    return response;
  }).catch(error => {
    return error;
  });
}

export async function postRecoverAccount(data) {
  const isMexEnv = /^(www.)*(mex.amlab7)\.com$/gi.test(location.hostname);
  const isPreProd = /^(www.)*(rc.amlab7)\.com$/gi.test(location.hostname);
  const isPreProdNew = /^(www.)*(new-rc.amlab7)\.com$/gi.test(location.hostname);
  const microEnv = getEnviroment() ? '-pd.aeromexico.com' : (isMexEnv || isPreProd || isPreProdNew) ? '-q.amlab7.com' : '-de.amlab7.com';
  const endpoint = `${POD_CODES.poflbk}${microEnv}/uc/recoveraccount`;
  const isCognito = false;
  const opts = {
    headers: {
      'pos': 'Web',
      'flow': 'Profile',
    },
  };

  return await doRequest(
    endpoint,
    opts,
    opts.headers,
    METHOD.POST,
    data,
    'MYB',
    isCognito,
    false,
    true,
  ).then(response => {
    return response;
  }).catch(error => {
    return error;
  });
}


export async function getSingleBilling(pnr, transactionId) {
  // Leer numberOfDecimalsBE del estado (proviene del servicio th-web-validations).
  // Solo se propaga a los microservicios si la bandera realmente llegó.
  const currentState = store.getState();
  const numberOfDecimalsBE = currentState?.Manage?.stateStoreFront?.numberOfDecimalsBE;
  const hasNumberOfDecimalsBE = numberOfDecimalsBE != null;

  const opts = {
    'channel': 'web',
    'Content-Type': 'application/json',
    ...(hasNumberOfDecimalsBE && { 'numberOfDecimalsBE': String(numberOfDecimalsBE) }),
    'x-transactionId': transactionId,
  };

  const isMexEnv = /^(www.)*(mex.amlab7)\.com$/gi.test(location.hostname);
  const isPreProd = /^(www.)*(rc.amlab7)\.com$/gi.test(location.hostname);
  const isPreProdNew = /^(www.)*(new-rc.amlab7)\.com$/gi.test(location.hostname);
  const microEnv = getEnviroment() ? '-pd.aeromexico.com' : (isMexEnv || isPreProd || isPreProdNew) ? '-q.amlab7.com' : '-de.amlab7.com';
  const endpoint = `${POD_CODES.slfcmbk}${ microEnv }/sb/singlebilling?reservationCode=${ pnr }`;
  const isCognito = true;

  return await doRequest(
    endpoint,
    opts,
    opts,
    METHOD.get,
    {},
    'MYB',
    isCognito,
    false,
    true,
    false,
  )
    .then((response) => {
      return response;
    })
    .catch((error) => {
      return error;
    });
}

export async function getStampingBilling(billingData) {
  const {
    regimenDescripcion,
    singleBilling,
    tipoRfcDescripcion,
    transactionId,
    values,
  } = billingData;

  // Leer numberOfDecimalsBE del estado (proviene del servicio th-web-validations).
  // Solo se propaga a los microservicios si la bandera realmente llegó.
  const currentState = store.getState();
  const numberOfDecimalsBE = currentState?.Manage?.stateStoreFront?.numberOfDecimalsBE;
  const hasNumberOfDecimalsBE = numberOfDecimalsBE != null;

  const opts = {
    'channel': 'web',
    'Content-Type': 'application/json',
    ...(hasNumberOfDecimalsBE && { 'numberOfDecimalsBE': String(numberOfDecimalsBE) }),
    'x-transactionId': transactionId,
  };

  let ticketPnr = [];
  ticketPnr = singleBilling?.ticketBillingArray.map(ticket => {
    return {
      eticket: ticket?.eticket,
      tipoDocumento: ticket?.tipoDocumento,
      tipoVenta: ticket?.tipoVenta,
    };
  });

  let fop = singleBilling?.ticketBillingArray.find(ticket => {
    return ticket?.fop.length > 0;
  });
  fop = fop !== undefined ? fop?.fop[0] : '';

  let ineComplement = [];
  if (values?.ineComplement) {
    ineComplement = [{
      idContabilidad: values?.accountingKey,
      version: '',
      tipoComite: values?.committeeType,
      tipoProceso: values?.processType,
      entidad: [{
        claveEntidad: values?.entity,
        ambito: values?.ambit,
        contabilidad: {
          idContabilidad: values?.accountingKey,
        },
      }],
    }];
  }

  const request = {
    tipoRfc: values?.typeOfRfc,
    tipoRfcDescripcion: tipoRfcDescripcion,
    razonSocial: values?.socialReason,
    regimenFiscal: values?.taxRegime,
    regimenDescripcion: regimenDescripcion,
    email: values?.billingMail,
    rfc: values?.rfc,
    codigoPostal: `${ values?.billingPostalCode }`,
    usoCFDI: values?.cfdiUse,
    ticketPnr: ticketPnr,
    uso_cfdiDescripcion: '', // eslint-disable-line
    ine: ineComplement,
    fop: fop,
    formaPagoDescripcion: '',
    recaptchaResponse: '',
    remember: false,
    pedido: '0',
  };

  const isMexEnv = /^(www.)*(mex.amlab7)\.com$/gi.test(location.hostname);
  const isPreProd = /^(www.)*(rc.amlab7)\.com$/gi.test(location.hostname);
  const isPreProdNew = /^(www.)*(new-rc.amlab7)\.com$/gi.test(location.hostname);
  const microEnv = getEnviroment() ? '-pd.aeromexico.com' : (isMexEnv || isPreProd || isPreProdNew) ? '-q.amlab7.com' : '-de.amlab7.com';
  const endpoint = `${POD_CODES.slfcmbk}${ microEnv }/sb/stampingbilling`;
  const isCognito = true;

  return await doRequest(
    endpoint,
    opts,
    opts,
    METHOD.POST,
    request,
    'MYB',
    isCognito,
    false,
    true,
    false,
  )
    .then((response) => {
      return response;
    })
    .catch((error) => {
      return error;
    });
}


/**
 * Construye la URL absoluta del micro mtpsbk para `/tc/reservation/v2/pnr`,
 * resolviendo el sufijo de ambiente igual que el resto del api-wrapper
 * (prod → -pd.aeromexico.com, qa/preprod → -q.amlab7.com, dev → -de.amlab7.com)
 * y anteponiendo el host de micros (`https://amx-c-`).
 *
 * @return {string} URL absoluta del endpoint v2.
 */
/** function getReservationV2Url() {
  const isMexEnv = /^(www.)*(mex.amlab7)\.com$/gi.test(location.hostname);
  const isPreProd = /^(www.)*(rc.amlab7)\.com$/gi.test(location.hostname);
  const isPreProdNew = /^(www.)*(new-rc.amlab7)\.com$/gi.test(location.hostname);
  const microEnv = getEnviroment() ? '-pd.aeromexico.com' : (isMexEnv || isPreProd || isPreProdNew) ? '-q.amlab7.com' : '-de.amlab7.com';
  const endpoint = `${POD_CODES.mtpsbk}${ microEnv }/api/v1.5/tc/reservation/v2/pnr`;
  return `${getHostApiMicros()}${endpoint}`;
}*/

/**
 * Consulta de reservación por PNR con token PingOne (WEB20-2144).
 *
 * Obtiene un token M2M de PingOne y lo envía en `Authorization: Bearer` al micro
 * mtpsbk `/tc/reservation/v2/pnr`. La respuesta 200 llega cifrada (texto, no
 * JSON), por lo que se usa `fetch` y se descifra antes de devolverla con el mismo
 * modelo que hoy consume el reducer de check-in.
 *
 * - 200: cuerpo cifrado → se descifra con `decryptReservationPayload`.
 * - 401: token revocado/ inválido → se limpia el token en memoria para que el
 *   siguiente intento pida uno nuevo.
 * - 400: JSON `{ channel, reason, httpCode }` → se propaga como error.
 *
 * @param {string} pnr - PNR o e-ticket.
 * @param {string} lastName - Apellido del pasajero.
 * @return {Promise<object>} Reservación descifrada, o un objeto de error/incidente.
 */
export async function getCheckInReservation(pnr, lastName) {
  const opts = {
    params: {
      pod: POD_CODES.mtpsbk,
    },
    headers: {
      'Content-Type': 'application/json',
      'lastName': `${lastName}`,
      'channel': 'WEB',
      'Access-Control-Allow-Origin': '*',
    },
    jsonExpected: true,
  };
  const isPnr = isPnrOrEticket(pnr);
  if (isPnr === 'pnr' || isPnr === null) {
    opts.headers = {
      ...opts.headers,
      'pnr': pnr,
    };
  }
  if (isPnr === 'eticket') {
    opts.headers = {
      ...opts.headers,
      'ticket': pnr,
    };
  }
  // Respeta la variable `environment` de api-config.js: si tiene valor usa la URL
  // de ese ambiente; si es null toma el host directo de la URL del navegador.
  const endpoint = getHostReservationPnr(window.location.host);
  const isCognito = true;

  return await doRequest(
    endpoint,
    opts,
    opts.headers,
    METHOD.GET,
    null,
    'MYB',
    isCognito,
    false,
    true,
    true,
    undefined,
    true
  ).then(async response => {
    // console.log('getCheckInReservation response:', response);
    // La respuesta 200 llega cifrada (texto Base64). Se descifra con
    // decryptReservationPayload para obtener el objeto de reservación en JSON.
    // Si viene envuelta en { data }, se desenvuelve primero.
    const payload = response != null && response.data != null
      ? response.data
      : response;
    // console.log('getCheckInReservation payload type:', typeof payload, payload);
    // Solo se descifra cuando el payload es un texto cifrado; si es un objeto
    // (p. ej. un cuerpo de error/incidente) se devuelve tal cual.
    if (typeof payload === 'string') {
      try {
        const decrypted = await decryptReservationPayload(payload);
        // console.log('getCheckInReservation decrypted payload:', decrypted);
        return decrypted;
      } catch (decryptError) {
        // console.log('getCheckInReservation decrypt error:', decryptError);
        return decryptError;
      }
    }
    return payload;
  }).catch(error => {
    return error;
  });
/** export async function getCheckInReservation(pnr, lastName) {
  try {
    const accessToken = await getPingOneAccessToken();

    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${ accessToken }`,
      'lastName': `${ lastName }`,
      'channel': 'WEB',
    };
    // Se conserva la lógica actual: pnr o ticket según isPnrOrEticket.
    if (isPnrOrEticket(pnr) === 'eticket') {
      headers.ticket = pnr;
    } else {
      headers.pnr = pnr;
    }

    const response = await fetch(getReservationV2Url(), { method: 'GET', headers });

    if (response.status === 401) {
      // Token revocado o inválido: el siguiente intento pedirá uno nuevo.
      clearPingOneAccessToken();
    }

    if (!response.ok) {
      // 400 → { channel, reason, httpCode }. Si no es JSON, se usa el status.
      const errorBody = await response.json().catch(() => ({ httpCode: response.status }));
      return errorBody;
    }

    const encrypted = await response.text();
    return await decryptReservationPayload(encrypted);
  } catch (error) {
    return error;
  } */
}
